import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * ספריית תבניות חוזי העבודה (מיגרציה 093).
 *
 * המילוי קורה בשרת ולא כאן. חלק מהשדות — ת"ז, פרטי בנק — יושבים
 * בטבלה שסגורה לגמרי ל API, ומילוי בדפדפן היה מחייב להוציא אותם
 * החוצה רק כדי להדביק אותם לטקסט.
 */

export type ContractTemplate = {
  id: string;
  name: string;
  description: string | null;
  contract_title: string | null;
  body: string;
  is_active: boolean;
  sort_order: number;
  job_title_ids: string[];
  /** מתאימה לתפקיד שלפיו נשאלה הרשימה. */
  suits: boolean;
};

export type TemplateField = { token: string; label: string; group: string };

export type RenderedContract = { title: string; body: string; missing: string[] };

const KEY = ['contract-templates'] as const;

/** p_job_title מסדר ראשונות את המתאימות, ולא מסתיר את השאר. */
export function useContractTemplates(jobTitleId: string | null) {
  return useQuery({
    queryKey: [...KEY, jobTitleId],
    queryFn: async (): Promise<ContractTemplate[]> => {
      const { data, error } = await supabase.rpc('contract_templates_for', {
        p_job_title: jobTitleId ?? undefined,
      });
      if (error) throw error;
      return (data ?? []) as unknown as ContractTemplate[];
    },
  });
}

/** כל התבניות למסך הניהול, כולל כבויות. */
export function useAllContractTemplates() {
  return useQuery({
    queryKey: [...KEY, 'all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contract_templates')
        .select(
          'id, name, description, contract_title, body, is_active, sort_order, contract_template_job_titles ( job_title_id )',
        )
        .order('sort_order')
        .order('name');
      if (error) throw error;
      return (data ?? []).map((row) => ({
        id: row.id,
        name: row.name,
        description: row.description,
        contract_title: row.contract_title,
        body: row.body,
        is_active: row.is_active,
        sort_order: row.sort_order,
        job_title_ids: (
          row.contract_template_job_titles as unknown as { job_title_id: string }[]
        ).map((link) => link.job_title_id),
      }));
    },
  });
}

/** המקומות השמורים שאפשר לשתול בתבנית. מקור אחד עם המילוי. */
export function useTemplateFields() {
  return useQuery({
    queryKey: ['contract-template-fields'],
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<TemplateField[]> => {
      const { data, error } = await supabase.rpc('contract_template_fields');
      if (error) throw error;
      return (data ?? []) as unknown as TemplateField[];
    },
  });
}

export type TemplateDraft = {
  id: string | null;
  name: string;
  description: string;
  contract_title: string;
  body: string;
  is_active: boolean;
  job_title_ids: string[];
};

export function emptyTemplate(): TemplateDraft {
  return {
    id: null,
    name: '',
    description: '',
    contract_title: '',
    body: '',
    is_active: true,
    job_title_ids: [],
  };
}

export function useTemplateMutations() {
  const queryClient = useQueryClient();
  const refresh = () => void queryClient.invalidateQueries({ queryKey: KEY });

  const save = useMutation({
    mutationFn: async (draft: TemplateDraft) => {
      const values = {
        name: draft.name.trim(),
        description: draft.description.trim() || null,
        contract_title: draft.contract_title.trim() || null,
        body: draft.body,
        is_active: draft.is_active,
      };

      let id = draft.id;
      if (id === null) {
        const { data, error } = await supabase
          .from('contract_templates')
          .insert(values)
          .select('id')
          .single();
        if (error) throw error;
        id = data.id;
      } else {
        const { error } = await supabase
          .from('contract_templates')
          .update({ ...values, updated_at: new Date().toISOString() })
          .eq('id', id);
        if (error) throw error;
      }

      // השיוך נכתב מחדש, כמו בערוצים ובמוצרים: המסך מציג מצב שלם
      const cleared = await supabase
        .from('contract_template_job_titles')
        .delete()
        .eq('template_id', id);
      if (cleared.error) throw cleared.error;

      if (draft.job_title_ids.length > 0) {
        const linked = await supabase.from('contract_template_job_titles').insert(
          draft.job_title_ids.map((jobTitleId) => ({
            template_id: id as string,
            job_title_id: jobTitleId,
          })),
        );
        if (linked.error) throw linked.error;
      }

      return id as string;
    },
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('contract_templates').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  return { save, remove };
}

/** תצוגה מקדימה של התבנית על עובד אמיתי, לפני שמוציאים. */
export function useRenderedContract(templateId: string | null, employeeId: string | null) {
  return useQuery({
    queryKey: ['rendered-contract', templateId, employeeId],
    enabled: Boolean(templateId) && Boolean(employeeId),
    gcTime: 0,
    queryFn: async (): Promise<RenderedContract> => {
      const { data, error } = await supabase.rpc('render_contract_template', {
        p_template: templateId as string,
        p_employee: employeeId as string,
      });
      if (error) throw error;
      return data as unknown as RenderedContract;
    },
  });
}

export function useCreateFromTemplate(employeeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (templateId: string) => {
      const { error } = await supabase.rpc('create_contract_from_template', {
        p_template: templateId,
        p_employee: employeeId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['employee-contracts', employeeId] });
      void queryClient.invalidateQueries({ queryKey: ['my-contracts'] });
    },
  });
}
